/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import { PlayerState } from "../videoplayer/enums";
import {
  DebugMetricGroupId,
  DebugOverlayMode,
  DebugPlaybackType
} from "./videoDebugOverlay.types";
import type {
  TAbrVariant,
  TDebugMetricField,
  TDebugMetricGroup,
  TDebugMetrics
} from "./videoDebugOverlay.types";

/**
 * Every pure helper behind the Video Debug Overlay, in the order a
 * snapshot passes through them:
 *
 *   create empty → fill (provider) → privacy filter → group projection
 *                                                   → clipboard export
 *
 * They live together because they are one pipeline and each stage's
 * output is the next one's input; splitting them bought five files and
 * an index for about four hundred lines of straight-line code. The
 * formatters come first because everything below depends on them.
 *
 * The only non-pure thing here is the clipboard write at the end, which
 * touches the DOM and the logger.
 */
// ── Formatting ──────────────────────────────────────────────────────────

/**
 * Rendered in place of any metric the platform could not supply, so a row
 * that is present but unknown is visually distinct from a row the current
 * mode does not include.
 */
export const NOT_AVAILABLE: string = "—";

const BITS_PER_KILOBIT: number = 1000;
const BYTES_PER_MEGABYTE: number = 1024 * 1024;
const BITS_PER_BYTE: number = 8;

function isUsableNumber(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

/**
 * Format a bit-rate as whole kilobits per second.
 *
 * @param bps - Rate in bits per second, or null.
 * @returns e.g. `1508 Kbps`, or the unavailable sentinel.
 */
export function formatKbps(bps: number | null): string {
  if (!isUsableNumber(bps)) {
    return NOT_AVAILABLE;
  }
  return `${Math.round(bps / BITS_PER_KILOBIT)} Kbps`;
}

/**
 * Format a pixel dimension pair.
 *
 * @param width - Width in pixels, or null.
 * @param height - Height in pixels, or null.
 * @returns e.g. `1280×720`, or the unavailable sentinel.
 */
export function formatResolution(
  width: number | null,
  height: number | null
): string {
  if (!isUsableNumber(width) || !isUsableNumber(height)) {
    return NOT_AVAILABLE;
  }
  return `${Math.round(width)}×${Math.round(height)}`;
}

/**
 * Format a duration in whole seconds.
 *
 * @param seconds - Duration in seconds, or null.
 * @returns e.g. `23 s`, or the unavailable sentinel.
 */
export function formatSeconds(seconds: number | null): string {
  if (!isUsableNumber(seconds)) {
    return NOT_AVAILABLE;
  }
  return `${Math.round(seconds)} s`;
}

/**
 * Format a byte count in megabytes to one decimal place.
 *
 * @param bytes - Size in bytes, or null.
 * @returns e.g. `4.2 MB`, or the unavailable sentinel.
 */
export function formatMegabytes(bytes: number | null): string {
  if (!isUsableNumber(bytes)) {
    return NOT_AVAILABLE;
  }
  return `${(bytes / BYTES_PER_MEGABYTE).toFixed(1)} MB`;
}

/**
 * Format the used / total memory pair.
 *
 * @param usedBytes - Used heap in bytes, or null.
 * @param totalBytes - Total heap in bytes, or null.
 * @returns e.g. `80.4 MB / 84.2 MB`, or the unavailable sentinel.
 */
export function formatMemory(
  usedBytes: number | null,
  totalBytes: number | null
): string {
  if (!isUsableNumber(usedBytes)) {
    return NOT_AVAILABLE;
  }
  if (!isUsableNumber(totalBytes)) {
    return formatMegabytes(usedBytes);
  }
  return `${formatMegabytes(usedBytes)} / ${formatMegabytes(totalBytes)}`;
}

/**
 * Format the dropped / decoded frame pair.
 *
 * @param dropped - Frames dropped, or null.
 * @param decoded - Frames decoded, or null.
 * @returns e.g. `0/11213`, or the unavailable sentinel.
 */
export function formatFrameCounts(
  dropped: number | null,
  decoded: number | null
): string {
  if (!isUsableNumber(dropped)) {
    return NOT_AVAILABLE;
  }
  const total: string = isUsableNumber(decoded)
    ? String(Math.round(decoded))
    : NOT_AVAILABLE;
  return `${Math.round(dropped)}/${total}`;
}

/**
 * Format a whole-number count.
 *
 * @param count - The count, or null.
 * @returns The count as a string, or the unavailable sentinel.
 */
export function formatCount(count: number | null): string {
  if (!isUsableNumber(count)) {
    return NOT_AVAILABLE;
  }
  return String(Math.round(count));
}

/**
 * Format a volume level as a percentage, noting mute.
 *
 * @param volume - Level from 0 to 1, or null.
 * @param isMuted - Whether output is muted.
 * @returns e.g. `100%` or `40% (muted)`, or the unavailable sentinel.
 */
export function formatVolume(volume: number | null, isMuted: boolean): string {
  if (!isUsableNumber(volume)) {
    return isMuted ? "muted" : NOT_AVAILABLE;
  }
  const percent: string = `${Math.round(volume * 100)}%`;
  return isMuted ? `${percent} (muted)` : percent;
}

/**
 * Coalesce a possibly-absent string to the unavailable sentinel.
 *
 * @param value - The string, or null.
 * @returns The trimmed string, or the unavailable sentinel when empty.
 */
export function formatText(value: string | null): string {
  if (value === null || value === undefined) {
    return NOT_AVAILABLE;
  }
  const trimmed: string = value.trim();
  return trimmed.length > 0 ? trimmed : NOT_AVAILABLE;
}

/**
 * Join the parts of an audio track description, skipping absent ones.
 *
 * @param language - BCP 47 tag, or null.
 * @param label - Human-readable label, or null.
 * @param channelsCount - Channel count, or null.
 * @returns e.g. `en · English · 2ch`, or the unavailable sentinel.
 */
export function formatAudioTrack(
  language: string | null,
  label: string | null,
  channelsCount: number | null
): string {
  const parts: Array<string> = [];
  // Truthiness rather than `!== null`: the values come from the player
  // and can arrive as undefined, which `!== null` would let through.
  if (language) {
    parts.push(language);
  }
  if (label && label !== language) {
    parts.push(label);
  }
  if (isUsableNumber(channelsCount)) {
    parts.push(`${Math.round(channelsCount)}ch`);
  }
  return parts.length > 0 ? parts.join(" · ") : NOT_AVAILABLE;
}

/**
 * Render one rung of the ABR ladder in the reference overlay's notation:
 * `{ 960x540@1334.048 Kbps }`, with the active rung wrapped as
 * `{*{ … }*}` so it stays identifiable in a screenshot where colour
 * highlighting has not survived.
 *
 * Bandwidth keeps its fractional kilobits here, unlike the headline
 * "Bitrate Used" row which rounds: the ladder is being read rung against
 * rung, and two rungs can differ by less than a whole Kbps.
 *
 * @param variant - The rung to render.
 * @returns e.g. `{*{ 1280x720@1508 Kbps }*}` for the active rung.
 */
export function formatAbrVariant(variant: TAbrVariant): string {
  const width: string = isUsableNumber(variant.width)
    ? String(Math.round(variant.width))
    : NOT_AVAILABLE;
  const height: string = isUsableNumber(variant.height)
    ? String(Math.round(variant.height))
    : NOT_AVAILABLE;
  const kbps: string = isUsableNumber(variant.bandwidthBps)
    ? String(Number((variant.bandwidthBps / BITS_PER_KILOBIT).toFixed(3)))
    : NOT_AVAILABLE;
  const body: string = `{ ${width}x${height}@${kbps} Kbps }`;
  return variant.isSelected ? `{*${body}*}` : body;
}

/**
 * Format a tri-state flag.
 *
 * `null` renders as the unavailable sentinel rather than as "No",
 * because "not answered yet" and "it is off" are different diagnoses and
 * conflating them is the kind of mistake this panel exists to prevent.
 *
 * @param value - True, false, or null when not yet known.
 * @returns `Yes`, `No`, or the unavailable sentinel.
 */
export function formatFlag(value: boolean | null): string {
  if (value === null) {
    return NOT_AVAILABLE;
  }
  return value ? "Yes" : "No";
}

/**
 * Format the combined resolution-and-framerate row the reference overlay
 * shows as a single field: `720p, 60fps`.
 *
 * Height alone rather than width×height, because the vertical line count
 * is how broadcast quality is named and it is what a QA report quotes.
 *
 * @param height - Active rendition height in pixels, or null.
 * @param frameRate - Active framerate, or null.
 * @returns e.g. `720p, 60fps`, or `720p` when the rate is unknown.
 */
export function formatResolutionRate(
  height: number | null,
  frameRate: number | null
): string {
  if (!isUsableNumber(height)) {
    return NOT_AVAILABLE;
  }
  const base: string = `${Math.round(height)}p`;
  if (!isUsableNumber(frameRate)) {
    return base;
  }
  return `${base}, ${Math.round(frameRate)}fps`;
}

/**
 * Format the bitrate together with how long the player has held it:
 * `1508 Kbps / 187 s`.
 *
 * The pair is one row because neither number means much alone — a low
 * bitrate held for three minutes is a stable ceiling, the same bitrate
 * held for two seconds is a ladder thrashing.
 *
 * @param bitrateBps - Active bitrate in bits per second, or null.
 * @param seconds - Seconds on the current rung, or null.
 * @returns e.g. `1508 Kbps / 187 s`, or just the bitrate when the dwell
 *          time is unknown.
 */
export function formatBitrateUsed(
  bitrateBps: number | null,
  seconds: number | null
): string {
  const bitrate: string = formatKbps(bitrateBps);
  if (!isUsableNumber(seconds)) {
    return bitrate;
  }
  return `${bitrate} / ${formatSeconds(seconds)}`;
}

/**
 * Format the combined codec and content-protection row:
 * `video/avc1.4D4020, audio/mp4a.40.2, ViewRight`.
 *
 * Parts that are unknown are left out rather than rendered as a sentinel,
 * so clear content reads `video/…, audio/…` and an audio-only stream reads
 * `audio/…` — in both cases the absence is the information.
 *
 * @param videoCodec - Active video codec string, or null.
 * @param audioCodec - Active audio codec string, or null.
 * @param drmScheme - Active DRM scheme name, or null for clear content.
 * @returns The joined description, or the unavailable sentinel when
 *          nothing at all is known.
 */
export function formatCodecAndDrm(
  videoCodec: string | null,
  audioCodec: string | null,
  drmScheme: string | null
): string {
  const parts: Array<string> = [];
  if (videoCodec !== null && videoCodec.trim().length > 0) {
    parts.push(`video/${videoCodec.trim()}`);
  }
  if (audioCodec !== null && audioCodec.trim().length > 0) {
    parts.push(`audio/${audioCodec.trim()}`);
  }
  if (drmScheme !== null && drmScheme.trim().length > 0) {
    parts.push(drmScheme.trim());
  }
  return parts.length > 0 ? parts.join(", ") : NOT_AVAILABLE;
}

/**
 * Estimate bytes buffered ahead from the buffered duration and the active
 * bitrate. Media Source Extensions reports no byte-level buffer occupancy,
 * so this is the only way to express buffer pressure in bytes at all.
 *
 * @param aheadSeconds - Seconds buffered ahead, or null.
 * @param bitrateBps - Active bitrate in bits per second, or null.
 * @returns Estimated bytes, or null when either input is unusable.
 */
export function estimateBufferedBytes(
  aheadSeconds: number | null,
  bitrateBps: number | null
): number | null {
  if (!isUsableNumber(aheadSeconds) || !isUsableNumber(bitrateBps)) {
    return null;
  }
  return (aheadSeconds * bitrateBps) / BITS_PER_BYTE;
}
// ── Empty snapshot ──────────────────────────────────────────────────────

/**
 * A snapshot in which nothing is known.
 *
 * Every caller that cannot produce real values returns this rather than a
 * partial object, so `TDebugMetrics` is never `Partial<…>` and no consumer
 * has to defend against a missing group. It is built fresh on each call
 * because the provider and the privacy filter both return new objects and
 * a shared frozen instance would be the one mutable-by-accident exception.
 *
 * @returns A fully-populated snapshot with every metric unset.
 */
export function createEmptyDebugMetrics(): TDebugMetrics {
  return {
    abr: {
      selectedVariantId: null,
      variants: []
    },
    audio: {
      channelsCount: null,
      codec: null,
      isMuted: false,
      label: null,
      language: null,
      volume: null
    },
    bandwidth: {
      bitrateBps: null,
      secondsOnCurrentBitrate: null,
      throughputBps: null
    },
    buffer: {
      aheadBytesEstimate: null,
      aheadSeconds: null
    },
    device: {
      appVersion: null,
      deviceId: null,
      lastKeyCode: null,
      localTime: null,
      memoryTotalBytes: null,
      memoryUsedBytes: null,
      platform: null
    },
    drm: {
      isHardwareAccelerationDetectable: false,
      isHardwareAccelerated: null,
      keyStatuses: [],
      keySystem: null,
      scheme: null
    },
    frames: {
      corrupted: null,
      decoded: null,
      dropped: null
    },
    identity: {
      channelCallSign: null,
      channelNumber: null,
      contentId: null,
      playbackType: DebugPlaybackType.UNKNOWN
    },
    playerState: {
      bufferingSubState: null,
      state: PlayerState.IDLE
    },
    source: {
      cdnHost: null,
      streamingUrl: null,
      thumbnailUrl: null
    },
    video: {
      codec: null,
      frameRate: null,
      height: null,
      optimalHeight: null,
      optimalWidth: null,
      width: null
    }
  };
}
// ── Group projection ────────────────────────────────────────────────────

/**
 * Projection of a snapshot onto the rows the overlay draws.
 *
 * This section owns the field table in one place: which metric belongs to
 * which group, what each row is labelled, and which of them compact mode
 * keeps. Putting it here rather than in the component is what lets the
 * clipboard export (UAW-3129) reuse the identical rows — a QA screenshot
 * and a pasted stats dump describe the same thing in the same words, so a
 * bug report can be read against either.
 *
 * **Compact mode is the reference overlay, row for row.** The ten fields
 * below, their labels and their value formats are taken from the IPTV
 * (Streamer) panel the proposal cites, so a tester moving between the two
 * reads the same thing in the same order:
 *
 *   Channel / Asset ID · Resolution / Rate · Connection Speed ·
 *   Bitrate Used · Buffer Available · Dropped Frames · Codec & DRM ·
 *   Variants · Streaming URL · Thumbnail URL
 *
 * Group order is what produces that sequence, so reordering the groups in
 * `buildDebugMetricGroups` reorders the panel. Expanded mode adds the rest
 * without disturbing it.
 */

/** Builds one row. */
function field(id: string, label: string, value: string): TDebugMetricField {
  return { id, label, value };
}

function buildIdentityFields(
  metrics: TDebugMetrics,
  isExpanded: boolean
): Array<TDebugMetricField> {
  const { channelCallSign, channelNumber, contentId, playbackType } =
    metrics.identity;

  // A tuned channel is identified the way the reference overlay does it —
  // "8117 KKPXDT2" — because that is the form a viewer report arrives in.
  // Non-linear playback has no channel, so it falls back to the asset id.
  const parts: Array<string> = [];
  if (channelNumber !== null && channelNumber.length > 0) {
    parts.push(channelNumber);
  }
  if (channelCallSign !== null && channelCallSign.length > 0) {
    parts.push(channelCallSign);
  }
  if (parts.length === 0 && contentId !== null) {
    parts.push(contentId);
  }

  const fields: Array<TDebugMetricField> = [
    field(
      "identity.content",
      "Channel / Asset ID",
      parts.length > 0 ? parts.join(" ") : NOT_AVAILABLE
    )
  ];

  if (isExpanded) {
    fields.push(field("identity.type", "Type", playbackType));
  }

  return fields;
}

function buildVideoFields(
  metrics: TDebugMetrics,
  isExpanded: boolean
): Array<TDebugMetricField> {
  const { frameRate, height, optimalHeight, optimalWidth } = metrics.video;

  const fields: Array<TDebugMetricField> = [
    field(
      "video.resolutionRate",
      "Resolution / Rate",
      formatResolutionRate(height, frameRate)
    )
  ];

  if (isExpanded) {
    fields.push(
      field(
        "video.optimal",
        "Optimal",
        formatResolution(optimalWidth, optimalHeight)
      )
    );
  }

  return fields;
}

/**
 * Audio contributes nothing to compact mode: the codec is already in the
 * combined "Codec & DRM" row, and track and volume are second questions.
 */
function buildAudioFields(metrics: TDebugMetrics): Array<TDebugMetricField> {
  const { channelsCount, isMuted, label, language, volume } = metrics.audio;

  return [
    field(
      "audio.track",
      "Audio Track",
      formatAudioTrack(language, label, channelsCount)
    ),
    field("audio.volume", "Volume", formatVolume(volume, isMuted))
  ];
}

function buildBandwidthFields(
  metrics: TDebugMetrics
): Array<TDebugMetricField> {
  const { bitrateBps, secondsOnCurrentBitrate, throughputBps } =
    metrics.bandwidth;

  // Throughput first, then the rate actually chosen against it — the order
  // the reference overlay uses, and the order the question is asked in.
  return [
    field(
      "bandwidth.throughput",
      "Connection Speed",
      formatKbps(throughputBps)
    ),
    field(
      "bandwidth.bitrate",
      "Bitrate Used",
      formatBitrateUsed(bitrateBps, secondsOnCurrentBitrate)
    )
  ];
}

function buildBufferFields(
  metrics: TDebugMetrics,
  isExpanded: boolean
): Array<TDebugMetricField> {
  const { aheadBytesEstimate, aheadSeconds } = metrics.buffer;

  const fields: Array<TDebugMetricField> = [
    field("buffer.seconds", "Buffer Available", formatSeconds(aheadSeconds))
  ];

  if (isExpanded) {
    const bytes: string = formatMegabytes(aheadBytesEstimate);
    fields.push(
      field(
        "buffer.bytes",
        "Buffer (est.)",
        bytes === NOT_AVAILABLE ? bytes : `~${bytes}`
      )
    );
  }

  return fields;
}

function buildFrameFields(
  metrics: TDebugMetrics,
  isExpanded: boolean
): Array<TDebugMetricField> {
  const { corrupted, decoded, dropped } = metrics.frames;

  const fields: Array<TDebugMetricField> = [
    field(
      "frames.dropped",
      "Dropped Frames",
      formatFrameCounts(dropped, decoded)
    )
  ];

  if (isExpanded) {
    fields.push(field("frames.corrupted", "Corrupted", formatCount(corrupted)));
  }

  return fields;
}

/**
 * Codecs and DRM share one row, as they do on the reference overlay: they
 * are read together when diagnosing "this stream will not decode here",
 * and splitting them into three rows spends three lines of a ten-line
 * panel on one question.
 */
function buildDrmFields(
  metrics: TDebugMetrics,
  isExpanded: boolean
): Array<TDebugMetricField> {
  const { keyStatuses, scheme } = metrics.drm;

  const fields: Array<TDebugMetricField> = [
    field(
      "drm.codecAndDrm",
      "Codec & DRM",
      formatCodecAndDrm(metrics.video.codec, metrics.audio.codec, scheme)
    )
  ];

  if (!isExpanded) {
    return fields;
  }

  fields.push(
    field(
      "drm.keyStatus",
      "Key Status",
      keyStatuses.length > 0 ? keyStatuses.join(", ") : NOT_AVAILABLE
    )
  );

  // Only platforms that can actually answer get the row. Everywhere else
  // it is left out rather than shown blank — see
  // TDrmMetrics.isHardwareAccelerationDetectable.
  if (metrics.drm.isHardwareAccelerationDetectable) {
    fields.push(
      field(
        "drm.hardwareAcceleration",
        "Hardware Accel",
        formatFlag(metrics.drm.isHardwareAccelerated)
      )
    );
  }

  return fields;
}

function buildAbrFields(metrics: TDebugMetrics): Array<TDebugMetricField> {
  const { variants } = metrics.abr;

  if (variants.length === 0) {
    return [field("abr.ladder", "Variants", NOT_AVAILABLE)];
  }

  // Rendered in the order the manifest declares, with the active rung
  // marked in place rather than moved to the end: where a rung sits in the
  // ladder is itself part of what the row is being read for.
  const rendered: string = variants
    .map((variant: TAbrVariant): string => formatAbrVariant(variant))
    .join(" ");

  return [field("abr.ladder", "Variants", rendered)];
}

function buildSourceFields(
  metrics: TDebugMetrics,
  isExpanded: boolean
): Array<TDebugMetricField> {
  const fields: Array<TDebugMetricField> = [
    field(
      "source.url",
      "Streaming URL",
      formatText(metrics.source.streamingUrl)
    )
  ];

  if (isExpanded) {
    // Parsed out of the streaming URL, so it sits directly beneath it.
    fields.push(
      field("source.cdn", "CDN Node", formatText(metrics.source.cdnHost))
    );
  }

  fields.push(
    field(
      "source.thumbnailUrl",
      "Thumbnail URL",
      formatText(metrics.source.thumbnailUrl)
    )
  );

  return fields;
}

function buildStateFields(metrics: TDebugMetrics): Array<TDebugMetricField> {
  const { bufferingSubState, state } = metrics.playerState;

  return [
    field("state.player", "State", state),
    field(
      "state.buffering",
      "Buffering",
      bufferingSubState === null ? NOT_AVAILABLE : bufferingSubState
    )
  ];
}

function buildDeviceFields(metrics: TDebugMetrics): Array<TDebugMetricField> {
  const {
    appVersion,
    deviceId,
    lastKeyCode,
    localTime,
    memoryTotalBytes,
    memoryUsedBytes,
    platform
  } = metrics.device;

  return [
    field("device.appVersion", "App", formatText(appVersion)),
    field("device.platform", "Platform", formatText(platform)),
    field(
      "device.memory",
      "Memory",
      formatMemory(memoryUsedBytes, memoryTotalBytes)
    ),
    field("device.time", "Time", formatText(localTime)),
    field("device.lastKeyCode", "Last Key", formatText(lastKeyCode)),
    field("device.id", "Device ID", formatText(deviceId))
  ];
}

/**
 * Project a snapshot onto the groups and rows the overlay renders.
 *
 * Groups that contribute no rows in the requested mode — Audio, State and
 * Device in compact mode — are omitted entirely rather than rendered
 * empty, so the panel never grows a heading with nothing under it.
 *
 * @param metrics - A snapshot from the metrics provider.
 * @param mode - Compact or expanded.
 * @returns Non-empty groups in display order.
 */
export function buildDebugMetricGroups(
  metrics: TDebugMetrics,
  mode: DebugOverlayMode
): ReadonlyArray<TDebugMetricGroup> {
  const isExpanded: boolean = mode === DebugOverlayMode.EXPANDED;

  const candidates: Array<TDebugMetricGroup> = [
    {
      id: DebugMetricGroupId.IDENTITY,
      label: "Identity",
      fields: buildIdentityFields(metrics, isExpanded)
    },
    {
      id: DebugMetricGroupId.VIDEO,
      label: "Video",
      fields: buildVideoFields(metrics, isExpanded)
    },
    {
      id: DebugMetricGroupId.AUDIO,
      label: "Audio",
      fields: isExpanded ? buildAudioFields(metrics) : []
    },
    {
      id: DebugMetricGroupId.BANDWIDTH,
      label: "Bandwidth",
      fields: buildBandwidthFields(metrics)
    },
    {
      id: DebugMetricGroupId.BUFFER,
      label: "Buffer",
      fields: buildBufferFields(metrics, isExpanded)
    },
    {
      id: DebugMetricGroupId.FRAMES,
      label: "Frames",
      fields: buildFrameFields(metrics, isExpanded)
    },
    {
      id: DebugMetricGroupId.DRM,
      label: "Codec & DRM",
      fields: buildDrmFields(metrics, isExpanded)
    },
    {
      id: DebugMetricGroupId.ABR,
      label: "ABR",
      fields: buildAbrFields(metrics)
    },
    {
      id: DebugMetricGroupId.SOURCE,
      label: "Source",
      fields: buildSourceFields(metrics, isExpanded)
    },
    {
      id: DebugMetricGroupId.STATE,
      label: "State",
      fields: isExpanded ? buildStateFields(metrics) : []
    },
    {
      id: DebugMetricGroupId.DEVICE,
      label: "Device",
      fields: isExpanded ? buildDeviceFields(metrics) : []
    }
  ];

  return candidates.filter(
    (group: TDebugMetricGroup): boolean => group.fields.length > 0
  );
}
// ── Clipboard export ────────────────────────────────────────────────────

const TAG: string = "exportDebugMetrics";

/** Heading printed above an exported snapshot so a paste is self-describing. */
const EXPORT_HEADER: string = "Video Debug Overlay — stats snapshot";

/**
 * Render the overlay's current rows as plain text for a bug report.
 *
 * Built from the same {@link TDebugMetricGroup} list the panel renders, so
 * the export is exactly what was on screen — including the effect of the
 * current mode and of the privacy filter. There is no second traversal of
 * the raw snapshot that could quietly widen what gets copied.
 *
 * @param groups - The groups currently rendered.
 * @returns A multi-line plain-text rendering, safe to paste into a ticket.
 */
export function formatDebugMetricsForExport(
  groups: ReadonlyArray<TDebugMetricGroup>
): string {
  const lines: Array<string> = [EXPORT_HEADER, new Date().toISOString(), ""];

  for (let i: number = 0; i < groups.length; i += 1) {
    const group: TDebugMetricGroup = groups[i];
    lines.push(`[${group.label}]`);
    for (let j: number = 0; j < group.fields.length; j += 1) {
      const entry: TDebugMetricField = group.fields[j];
      lines.push(`  ${entry.label}: ${entry.value}`);
    }
    lines.push("");
  }

  return lines.join("\n");
}

/**
 * Copy `text` using the legacy selection-based clipboard path.
 *
 * `navigator.clipboard` is Chrome 66+ and banned by the Chromium 52
 * compatibility gate (docs/CHROMIUM-52-COMPATIBILITY.md), and the Smart TV
 * browsers this overlay most needs to work on are exactly the ones below
 * that line — so the deprecated API is the only one available, and it
 * still works everywhere else.
 *
 * The textarea is positioned off-screen rather than hidden with
 * `display: none`, because a non-rendered element cannot hold a selection
 * and the copy silently does nothing.
 */
function copyViaExecCommand(text: string): boolean {
  if (typeof document === "undefined" || document.body === null) {
    return false;
  }

  const textArea: HTMLTextAreaElement = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "true");
  textArea.setAttribute("aria-hidden", "true");
  textArea.style.position = "fixed";
  textArea.style.top = "-1000px";
  textArea.style.left = "-1000px";
  textArea.style.opacity = "0";

  document.body.appendChild(textArea);
  try {
    textArea.select();
    textArea.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch (error: unknown) {
    Log.w(TAG, "copyViaExecCommand() — copy refused", error);
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

/**
 * Put the rendered snapshot on the clipboard, falling back to the log.
 *
 * The fallback is the point, not an afterthought: a TV browser may have no
 * usable clipboard at all, and a QA engineer reading the numbers out of
 * the log is a worse experience than a copy but still gets them into the
 * ticket, which is the whole purpose (UAW-3129).
 *
 * @param text - The rendering from {@link formatDebugMetricsForExport}.
 * @returns True when the clipboard accepted it, false when it was logged.
 */
export function copyDebugMetricsToClipboard(text: string): boolean {
  if (copyViaExecCommand(text)) {
    Log.i(TAG, "copyDebugMetricsToClipboard() — copied to clipboard");
    return true;
  }

  // Deliberately Log.i rather than console.log: the app's own logger is
  // what QA already has open on a TV, and it is the sink the Diagnostics
  // pipeline forwards. The whole snapshot goes in one entry so it can be
  // copied out as a block.
  Log.i(TAG, `copyDebugMetricsToClipboard() — snapshot follows\n${text}`);
  return false;
}
